import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { inventoryItemAPI } from '../api/inventoryItemAPI';
import { getParentDestinationOptions, getSubcategoryOptions } from '../utils/issueUtils';
import { formatErrorResponse } from '../utils/errorHandlers';
import { usePermissions } from '../contexts/PermissionContext';
import Select from 'react-select';
import QuantityPicker from './QuantityPicker';
import '../styles/modal.css';

function BulkIssueModal({ show, onHide, selectedItems, warehouse, onSuccess }) {
    const { hasPermission } = usePermissions();
    const [parentDestination, setParentDestination] = useState('');
    const [destination, setDestination] = useState(''); // This will be subcategory ID
    const [comments, setComments] = useState('');
    const [itemSellPrices, setItemSellPrices] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedItemDetails, setSelectedItemDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [itemQuantities, setItemQuantities] = useState({});
    const [activeItems, setActiveItems] = useState([]);
    const [parentDestinationOptions, setParentDestinationOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [destinationsLoading, setDestinationsLoading] = useState(true);
    const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
    
    // Load parent destination options when modal is shown
    useEffect(() => {
        const loadDestinations = async () => {
            try {
                setDestinationsLoading(true);
                const options = await getParentDestinationOptions();
                setParentDestinationOptions(options);
            } catch (error) {
                console.error('Error loading destinations:', error);
                setError('Failed to load destination options');
            } finally {
                setDestinationsLoading(false);
            }
        };

        if (show) {
            loadDestinations();
        }
    }, [show]);

    // Load subcategories when parent destination changes
    useEffect(() => {
        const loadSubcategories = async () => {
            if (parentDestination) {
                try {
                    setSubcategoriesLoading(true);
                    const options = await getSubcategoryOptions(parentDestination);
                    setSubcategoryOptions(options);
                } catch (error) {
                    console.error('Error loading subcategories:', error);
                    setSubcategoryOptions([{ value: '', label: 'Error loading subcategories', type: 'error' }]);
                } finally {
                    setSubcategoriesLoading(false);
                }
            } else {
                setSubcategoryOptions([]);
                setDestination(''); // Clear subcategory selection
            }
        };
        loadSubcategories();
    }, [parentDestination]);
    
    // Initialize active items, quantities, and sell prices when selectedItems change
    useEffect(() => {
        if (selectedItems && selectedItems.length > 0) {
            setActiveItems([...selectedItems]);
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);
            const initialSellPrices = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemSellPrices(initialSellPrices);
        }
    }, [selectedItems]);
    
    // Fetch item details when the modal opens
    useEffect(() => {
        const fetchData = async () => {
            if (!show) return;
            
            try {
                setIsLoading(true);
                setSelectedItemDetails([]); // Clear existing data
                
                // Fetch item details
                if (selectedItems.length > 0) {
                    const itemIds = selectedItems.map(item => item.id).join(',');
                    const response = await inventoryItemAPI.getByIds(itemIds);
                    
                    // Extract items array from response
                    const items = response?.results || [];
                    
                    if (items.length > 0) {
                        setSelectedItemDetails(items);
                    } else {
                        setError('Failed to load item details - no data returned');
                        setSelectedItemDetails([]);
                    }
                }
            } catch (error) {
                setError(formatErrorResponse(error));
                setSelectedItemDetails([]);
            } finally {
                setIsLoading(false);
            }
        };
        
        if (show) {
            // Reset state when modal opens
            setParentDestination('');
            setDestination('');
            setComments('');
            setError('');
            setIsSubmitting(false);
            setActiveItems([...selectedItems]); // Reset active items to original selection
            
            // Reset item quantities and sell prices to empty values
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);
            const initialSellPrices = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemSellPrices(initialSellPrices);
            
            fetchData();
        }
    }, [show, selectedItems]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!parentDestination) {
            setError('Please specify a destination');
            return;
        }

        // Check if subcategory is required (when parent has subcategories)
        const hasSubcategories = subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error');
        if (hasSubcategories && !destination) {
            setError('Please select a subcategory - this destination has subcategories available');
            return;
        }
        
        // Create items to issue with quantities and sell prices from local state
        const itemsToIssue = activeItems.map(item => ({
            id: item.id,
            quantity: parseFloat(itemQuantities[item.id]) || 0,
            sell_price: itemSellPrices[item.id] ? parseFloat(itemSellPrices[item.id]) : null
        }));
        
        // Check if quantities are valid
        const hasInvalidQuantity = itemsToIssue.some(item => 
            !item.quantity || 
            isNaN(item.quantity) || 
            item.quantity <= 0
        );
        
        if (hasInvalidQuantity) {
            setError('Quantity must be a positive number for all items');
            return;
        }
        
        try {
            setIsSubmitting(true);
            
            
            await inventoryItemAPI.issueBulk(itemsToIssue, parentDestination, destination, comments);
            setIsSubmitting(false);
            onSuccess(); // Only called after successful issue
            onHide();
        } catch (error) {
            console.error('Error issuing items:', error);
            setError(formatErrorResponse(error));
            setIsSubmitting(false);
        }
    };
    
    const handleQuantityChange = (id, value) => {
        // Update quantities locally without calling parent's onSuccess
        setItemQuantities(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSellPriceChange = (id, value) => {
        // Allow only up to 2 decimal places
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
            setItemSellPrices(prev => ({
                ...prev,
                [id]: value
            }));
        }
    };

    const handleRemoveItem = (itemId) => {
        // Don't allow removing the last item
        if (activeItems.length <= 1) {
            return;
        }
        
        // Remove item from activeItems
        setActiveItems(prev => prev.filter(item => item.id !== itemId));
        
        // Remove quantity and sell price entries for this item
        setItemQuantities(prev => {
            const newQuantities = { ...prev };
            delete newQuantities[itemId];
            return newQuantities;
        });
        setItemSellPrices(prev => {
            const newSellPrices = { ...prev };
            delete newSellPrices[itemId];
            return newSellPrices;
        });
    };
    
    // Find item details safely
    const findItemDetail = React.useCallback((itemId) => {
        if (!Array.isArray(selectedItemDetails)) {
            return null;
        }
        
        const itemFromDetails = selectedItemDetails.find(detail => detail._id === itemId);
        return itemFromDetails || null;
    }, [selectedItemDetails]);
    
    // Check permission
    if (show && !hasPermission('inventory.issue')) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Permission Denied</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        You don&apos;t have permission to issue inventory items.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Early return if no warehouse information
    if (show && !warehouse) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        No warehouse information available. Please select items from the same warehouse.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
    
    return (
        <Modal 
            show={show} 
            onHide={() => {
                setItemQuantities({});
                setSelectedItemDetails([]);
                setError('');
                onHide();
            }} 
            size="xl"
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton className="bulk-modal-header">
                    <Modal.Title>
                        <i className="bi bi-box-arrow-up bulk-modal-icon"></i>
                        Issue Items
                        <span className="bulk-modal-badge bulk-modal-badge-info">
                            {activeItems.length}
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    
                    <div className="mb-4">
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading item details...</span>
                                </Spinner>
                            </div>
                        ) : (
                            <div className="bulk-table-wrapper">
                                <table className="table table-sm align-middle bulk-table bulk-table-info">
                                    <thead>
                                        <tr>
                                            <th>GTIN</th>
                                            <th>Description</th>
                                            <th>Current Location</th>
                                            <th>Available</th>
                                            <th>Quantity to Issue</th>
                                            <th>Sell Price</th>
                                            <th style={{width: '50px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeItems.map(item => {
                                            const itemDetail = findItemDetail(item.id);
                                            return (
                                                <tr key={item.id}>
                                                    <td>{itemDetail?.stock_item?.gtin ?
                                                        <code>{itemDetail.stock_item.gtin}</code> :
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.stock_item?.description || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.location_name || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.quantity || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>
                                                        <QuantityPicker
                                                            casePack={itemDetail?.stock_item?.case_pack || 1}
                                                            uom={itemDetail?.stock_item?.uom || ''}
                                                            value={itemQuantities[item.id] || ''}
                                                            onChange={(qty) => handleQuantityChange(item.id, qty)}
                                                            required
                                                            max={itemDetail?.quantity}
                                                        />
                                                        {!itemDetail && (
                                                            <small className="text-muted">Adjust filters to see this item</small>
                                                        )}
                                                    </td>
                                                    <td style={{maxWidth: '100px'}}>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={itemSellPrices[item.id] || ''}
                                                            onChange={(e) => handleSellPriceChange(item.id, e.target.value)}
                                                            size="sm"
                                                            disabled={!itemDetail}
                                                            placeholder="$"
                                                            autoComplete="off"
                                                            spellCheck="false"
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="bulk-remove-btn"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            disabled={activeItems.length <= 1}
                                                            title={activeItems.length <= 1 ? "Cannot remove the last item" : "Remove item"}
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    <div className="bulk-section-sep bulk-section-sep-info">
                        <span className="bulk-section-sep-label">
                            <i className="bi bi-send me-1"></i> Issue Details
                        </span>
                    </div>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Warehouse</Form.Label>
                            <div className="form-control bg-body-secondary">
                                {warehouse ? warehouse.name : 'No warehouse selected'}
                            </div>
                        </Form.Group>
                    </Col>
                    
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Destination</Form.Label>
                                <Select
                                    value={parentDestinationOptions.find(option => option.value === parentDestination)}
                                    onChange={(selectedOption) => {
                                        setParentDestination(selectedOption ? selectedOption.value : '');
                                        setDestination(''); // Clear subcategory when parent changes
                                    }}
                                    options={parentDestinationOptions}
                                    isSearchable
                                    isClearable
                                    isLoading={destinationsLoading}
                                    isDisabled={destinationsLoading || isLoading || isSubmitting}
                                    placeholder={destinationsLoading ? "Loading..." : "Select destination..."}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                                <Form.Text className="text-muted">
                                    Select the main destination
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Subcategory{subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') ? '*' : ''}
                                </Form.Label>
                                <Select
                                    value={subcategoryOptions.find(option => option.value === destination)}
                                    onChange={(selectedOption) => setDestination(selectedOption ? selectedOption.value : '')}
                                    options={subcategoryOptions}
                                    isSearchable
                                    isClearable={!subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error')}
                                    isLoading={subcategoriesLoading}
                                    isDisabled={!parentDestination || subcategoriesLoading || isLoading || isSubmitting}
                                    placeholder={!parentDestination ? "Select destination first" : subcategoriesLoading ? "Loading..." : 
                                        subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') ? 
                                        "Select subcategory..." : "No subcategories available"}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    required={subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error')}
                                />
                                <Form.Text className="text-muted">
                                    {subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') ? 
                                        'Required: Select a specific subcategory' : 
                                        'Optional: Select a specific subcategory'}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Comments</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any comments about this issue..."
                            autoComplete="off"
                            spellCheck="true"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    {error && <Alert variant="danger" className="mb-3 w-100">{error}</Alert>}
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={
                        isSubmitting || 
                        !parentDestination || 
                        (subcategoryOptions.some(option => option.type !== 'placeholder' && option.type !== 'error') && !destination)
                    }>
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Issuing...
                            </>
                        ) : (
                            'Issue Items'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default BulkIssueModal; 